import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import { useClientTranslations } from "../../lib/i18n/client";
import type { JourneyStep } from "../../types/journey";
import styles from "./StepActionButtons.module.css";

interface StepActionButtonsProps {
	step: JourneyStep | null;
	/**
	 * Optional override for the Ask Eve handler. When omitted, the component
	 * calls setPrefillQuestion(prefill) + navigate("/chat") via the default
	 * handler (D-11). Phase 43 reuses this component below the FAQ section
	 * with the same defaults — no override needed there.
	 */
	onAskEve?: (prefill: string) => void;
	/**
	 * Optional override for the Call Digital Nurse handler. When omitted, the
	 * component fires a sonner toast.success with the comingSoon i18n string
	 * (D-01, D-13, D-15) — placeholder only, no real call wiring this phase.
	 */
	onCallNurse?: () => void;
	/**
	 * Optional analytics callback. Fires BEFORE the action handler (override or
	 * default) so the parent can record analytics without forcing this
	 * component to import `useAnalytics`. Phase 43 D-23 (page-level Ask Eve)
	 * and D-26 (page/faq-level Call Digital Nurse) wire this from
	 * `Journey.tsx` and `CommonQuestions.tsx`.
	 */
	onAnalyticsEvent?: (action: "ask_eve" | "call_nurse") => void;
}

export function StepActionButtons({
	step,
	onAskEve,
	onCallNurse,
	onAnalyticsEvent,
}: StepActionButtonsProps) {
	const { t } = useClientTranslations("journey");
	const navigate = useNavigate();
	const { setPrefillQuestion } = useChatActions();

	const isDisabled = step === null;

	const handleAskEve = () => {
		if (!step) return;
		// Prefer the step's curated askEvePrompt (translated in fixtures); fall back
		// to the generic template for legacy fixtures that don't carry one.
		const prefill =
			step.askEvePrompt && step.askEvePrompt.trim().length > 0
				? step.askEvePrompt
				: t("askEve.prefillQuestion", { step: step.title });
		if (onAnalyticsEvent) onAnalyticsEvent("ask_eve");
		if (onAskEve) {
			onAskEve(prefill);
			return;
		}
		setPrefillQuestion(prefill);
		navigate("/chat");
	};

	const handleCallNurse = () => {
		if (isDisabled) return;
		if (onAnalyticsEvent) onAnalyticsEvent("call_nurse");
		if (onCallNurse) {
			onCallNurse();
			return;
		}
		toast.success(t("callDigitalNurse.comingSoon"));
	};

	return (
		<div className={styles.container} data-step-content="action-buttons">
			<button
				type="button"
				className={styles.askEve}
				onClick={handleAskEve}
				disabled={isDisabled}
				aria-label={t("actions.askEve")}
			>
				<img
					src="/images/education/icon-ask-eve.svg"
					alt=""
					className={styles.icon}
					aria-hidden="true"
				/>
				<span>{t("actions.askEve")}</span>
			</button>

			<button
				type="button"
				className={styles.callNurse}
				onClick={handleCallNurse}
				disabled={isDisabled}
				aria-label={t("actions.callDigitalNurse")}
			>
				<span>{t("actions.callDigitalNurse")}</span>
			</button>
		</div>
	);
}
